import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Star, ArrowLeft, Briefcase, LayoutTemplate, Loader2, AlertCircle, UserPlus, UserCheck, Flag,
} from 'lucide-react';

import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import VerifiedBadge from '../components/shared/VerifiedBadge';
import ReportModal from '../components/marketplace/ReportModal';
import { loginPath } from '../lib/authRedirect';

const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const imgUrl = (src) => {
  if (!src) return null;
  if (src.startsWith('http')) return src;
  return `${API_URL}${src}`;
};

const StarRating = ({ rating, count }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-100'}`}
      />
    ))}
    {count !== undefined && (
      <span className="text-sm text-gray-500 ml-1">
        {rating > 0 ? `${rating.toFixed(1)} (${count})` : 'No reviews'}
      </span>
    )}
  </div>
);

const TemplateCard = ({ template, onClick }) => {
  const [imgOk, setImgOk] = useState(true);
  const thumb = imgUrl(template.thumbnail);
  const isFree = template.licensing_type === 'free' || !template.price_cents;

  return (
    <div
      onClick={() => onClick(template)}
      className="group cursor-pointer bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden"
    >
      <div className="relative bg-gray-50" style={{ aspectRatio: '4/3' }}>
        {thumb && imgOk ? (
          <img src={thumb} alt={template.name} className="w-full h-full object-cover" onError={() => setImgOk(false)} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <LayoutTemplate className="w-8 h-8 text-gray-300" />
          </div>
        )}
        <span className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${isFree ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}`}>
          {isFree ? 'Free' : `$${(template.price_cents / 100).toFixed(2)}`}
        </span>
      </div>
      <div className="p-3">
        <p className="font-medium text-gray-900 text-sm truncate">{template.name}</p>
        <div className="flex items-center gap-1 mt-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className={`w-3 h-3 ${i <= Math.round(template.average_rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-100'}`} />
          ))}
          {(template.review_count || 0) > 0 && (
            <span className="text-xs text-gray-400 ml-0.5">({template.review_count})</span>
          )}
        </div>
      </div>
    </div>
  );
};

const StatBox = ({ label, value }) => (
  <div className="text-center">
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 mt-0.5">{label}</p>
  </div>
);

export default function MarketplaceCreatorPage() {
  const { creatorId } = useParams();
  const navigate = useNavigate();

  const [creator, setCreator] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesTotal, setTemplatesTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [followLoading, setFollowLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const LIMIT = 12;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API_URL}/api/marketplace/creators/${creatorId}`, { headers });
        if (!r.ok) {
          setError(r.status === 404 ? 'Creator not found.' : 'Failed to load creator.');
          return;
        }
        const data = await r.json();
        setCreator(data);
        setFollowing(data.is_following || false);
        setFollowersCount(data.followers_count || 0);
      } catch {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [creatorId]);

  const handleFollow = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) { navigate(loginPath(window.location.pathname + window.location.search)); return; }
    setFollowLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/marketplace/creators/${creatorId}/follow`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setFollowing(d.following);
        setFollowersCount(d.followers_count);
      }
    } catch {}
    setFollowLoading(false);
  };

  useEffect(() => {
    if (!creator) return;
    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const r = await fetch(
          `${API_URL}/api/marketplace/templates?creator_id=${creatorId}&page=${page}&limit=${LIMIT}`
        );
        if (r.ok) {
          const d = await r.json();
          setTemplates((prev) => page === 1 ? (d.items || []) : [...prev, ...(d.items || [])]);
          setTemplatesTotal(d.total || 0);
          setHasMore(page < (d.pages || 1));
        }
      } catch {
        // swallow
      } finally {
        setTemplatesLoading(false);
      }
    };
    loadTemplates();
  }, [creator, creatorId, page]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{error}</h2>
          <button onClick={() => navigate('/marketplace')} className="mt-4 text-sm text-violet-600 hover:underline">
            Back to Marketplace
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const initials = (creator.display_name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-gray-500">
          <button onClick={() => navigate('/marketplace')} className="hover:text-violet-600 transition-colors flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Marketplace
          </button>
          <span>/</span>
          <span className="text-gray-800 font-medium">Creator</span>
        </div>
      </div>

      {/* Creator hero */}
      <div className="bg-gradient-to-br from-gray-900 via-violet-950 to-fuchsia-950 text-white py-14 px-4">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Avatar */}
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center text-white font-extrabold text-3xl shadow-xl flex-shrink-0">
            {initials}
          </div>

          {/* Info */}
          <div className="flex-1 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
              <h1 className="text-2xl sm:text-3xl font-extrabold inline-flex items-center gap-2">
                {creator.display_name}
                <VerifiedBadge verified={creator.verified} size="md" showLabel />
              </h1>
              {creator.featured_creator && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-400/20 text-amber-300 border border-amber-400/30 px-2.5 py-1 rounded-full">
                  ⭐ Featured Creator
                </span>
              )}
              {creator.available_for_hire && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 px-2.5 py-1 rounded-full">
                  <Briefcase className="w-3 h-3" /> Available for Hire
                </span>
              )}
            </div>
            {creator.bio && (
              <p className="text-white/70 text-sm leading-relaxed max-w-lg mb-4">{creator.bio}</p>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
              <StarRating rating={creator.average_rating} count={creator.review_count} />
              <button
                onClick={handleFollow}
                disabled={followLoading}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-60 ${
                  following
                    ? 'bg-white/10 text-white/80 hover:bg-white/15 border border-white/20'
                    : 'bg-violet-500 hover:bg-violet-400 text-white shadow-lg shadow-violet-900/30'
                }`}
              >
                {following ? <UserCheck className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                {following ? `Following · ${followersCount}` : `Follow · ${followersCount}`}
              </button>
              <button
                onClick={() => setReportOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-red-300 transition-colors"
              >
                <Flag className="w-3.5 h-3.5" /> Report creator
              </button>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="max-w-4xl mx-auto mt-8 grid grid-cols-3 gap-6 bg-white/10 border border-white/15 rounded-2xl px-6 py-5">
          <StatBox label="Templates" value={creator.templates_count} />
          <StatBox label="Total Sales" value={creator.sales_count.toLocaleString()} />
          <StatBox
            label="Avg Rating"
            value={creator.average_rating > 0 ? creator.average_rating.toFixed(1) : '—'}
          />
        </div>
      </div>

      {/* Templates */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            Templates{templatesTotal > 0 && <span className="text-gray-400 font-normal text-base ml-2">({templatesTotal})</span>}
          </h2>
        </div>

        {templates.length === 0 && !templatesLoading ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <LayoutTemplate className="w-12 h-12 text-gray-300 mb-4" />
            <p className="text-gray-500">No listed templates yet.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
              {templates.map((t) => (
                <TemplateCard key={t.id} template={t} onClick={(t) => navigate(`/marketplace/templates/${t.id}`)} />
              ))}
            </div>

            {templatesLoading && (
              <div className="flex justify-center mt-8">
                <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
              </div>
            )}

            {hasMore && !templatesLoading && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={() => setPage((p) => p + 1)}
                  className="px-6 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:border-violet-400 hover:text-violet-700 transition-all shadow-sm"
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer />

      {reportOpen && creator && (
        <ReportModal type="creator" targetId={creator.id} onClose={() => setReportOpen(false)} />
      )}
    </div>
  );
}
